/**
 * SkeletonCard Component - Card Loading Placeholder
 *
 * Animated skeleton loader for card-based layouts.
 * Provides visual feedback during data loading without blocking the UI.
 *
 * @example
 * // Single card skeleton
 * <SkeletonCard />
 *
 * @example
 * // Multiple card skeletons
 * <SkeletonCard count={3} />
 *
 * @param {Object} props
 * @param {number} props.count - Number of skeleton cards to render (default: 1)
 *
 * @returns {JSX.Element} SkeletonCard component(s)
 */

import React from 'react';
import PropTypes from 'prop-types';
import '../styles/Skeletons.css';

const SkeletonCard = ({ count = 1 }) => {
  // Handle edge cases
  if (count <= 0) {
    return null;
  }

  // Generate array of skeleton cards
  const cards = [...Array(count)].map((_, index) => (
    <div key={`skeleton-card-${index}`} className="skeleton-card">
      {/* Card Header with Avatar */}
      <div className="skeleton-card-header">
        <div className="skeleton skeleton-avatar"></div>
        <div style={{ flex: 1 }}>
          <div
            className="skeleton skeleton-text-lg"
            style={{ width: '60%' }}
          ></div>
          <div
            className="skeleton skeleton-text"
            style={{ width: '40%' }}
          ></div>
        </div>
      </div>

      {/* Card Body with Text Lines */}
      <div className="skeleton-card-body">
        <div
          className="skeleton skeleton-text"
          style={{ width: '100%' }}
        ></div>
        <div
          className="skeleton skeleton-text"
          style={{ width: '90%' }}
        ></div>
        <div
          className="skeleton skeleton-text"
          style={{ width: '80%' }}
        ></div>
      </div>
    </div>
  ));

  return <>{cards}</>;
};

SkeletonCard.propTypes = {
  count: PropTypes.number
};

SkeletonCard.defaultProps = {
  count: 1
};

export default SkeletonCard;
